export type PendingCheckout = {
  schema: 1;
  checkoutId: string;
  company: string;
  contact: string;
  kind: 'pedido' | 'pix';
  fingerprint: string;
  request: Record<string, any>;
  createdAt: string;
};

const prefix = 'xeguei.pendingCashbackCheckout.v1:';
const normalizeCompany = (value: string) => decodeURIComponent(value);
const normalizeContact = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits.length === 10 || digits.length === 11 ? `55${digits}` : digits;
};
const keyFor = (company: string, contact: string) => `${prefix}${JSON.stringify([normalizeCompany(company), normalizeContact(contact)])}`;
const storageError = () => new Error('Não foi possível guardar ou consultar seu pedido neste aparelho. Verifique seus pedidos antes de tentar novamente.');

function canonical(value: any): any {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().filter(key => value[key] !== undefined).map(key => [key, canonical(value[key])]));
  return value;
}

/** Bind every reviewed choice, including identity, address details and payment, to one operation. */
export function checkoutFingerprint(body: Record<string, any>): string {
  const { checkoutId, checkoutRecovery, ...reviewed } = body;
  return JSON.stringify(canonical(reviewed));
}

export function readPendingCheckout(company: string, contact: string, storage: Storage = localStorage): PendingCheckout | null {
  try {
    const raw = storage.getItem(keyFor(company, contact));
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (value?.schema !== 1 || value.company !== normalizeCompany(company) || value.contact !== normalizeContact(contact)
      || typeof value.checkoutId !== 'string' || !value.request || !['pedido', 'pix'].includes(value.kind)
      || value.fingerprint !== checkoutFingerprint(value.request)) throw storageError();
    return value;
  } catch { throw storageError(); }
}

export function savePendingCheckout(body: Record<string, any>, kind: PendingCheckout['kind'], storage: Storage = localStorage): PendingCheckout {
  const company = normalizeCompany(body.empresaid);
  const contact = normalizeContact(body.telefoneCliente);
  // Never replace an uncertain operation, even if the current cart or customer details changed.
  if (readPendingCheckout(company, contact, storage)) throw new Error('Consulte o pedido pendente antes de enviar outro.');
  const checkoutId = window.crypto.randomUUID().replace(/-/g, '');
  const request = JSON.parse(JSON.stringify({ ...body, checkoutId }));
  const pending: PendingCheckout = { schema: 1, company, contact, kind, checkoutId, request,
    fingerprint: checkoutFingerprint(request), createdAt: new Date().toISOString() };
  try {
    storage.setItem(keyFor(company, contact), JSON.stringify(pending));
    if (readPendingCheckout(company, contact, storage)?.checkoutId !== checkoutId) throw storageError();
  } catch { throw storageError(); }
  return pending;
}

export function clearPendingCheckout(pending: PendingCheckout, storage: Storage = localStorage): void {
  try {
    if (readPendingCheckout(pending.company, pending.contact, storage)?.checkoutId === pending.checkoutId) {
      storage.removeItem(keyFor(pending.company, pending.contact));
    }
  } catch { throw storageError(); }
}

export function checkoutRecoveryRequest(pending: PendingCheckout) {
  return { empresaid: pending.company, telefoneCliente: pending.contact, checkoutId: pending.checkoutId,
    resgateCashBack: true, checkoutRecovery: true };
}
